import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-daily-goal-date-to-achievement',
  templateUrl: './daily-goal-date-to-achievement.component.html',
  styleUrls: ['./daily-goal-date-to-achievement.component.sass']
})
export class DailyGoalDateToAchievementComponent implements OnChanges {
  @Input() rows: any[] = [];
  @Input() field: any;
  @Output() onChange = new EventEmitter<any[]>();
  @Input() readonly: boolean = false;

  newDate: Date | null = null;
  newAchievement: any = null;
  searchResults: any[] = [];
  errorMessage: string | null = null;

  // Resolved achievement titles keyed by numeric id (rows only store the id).
  titlesById: { [id: number]: string } = {};

  constructor(private dataService: DataService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rows']) {
      this.loadTitles();
    }
  }

  get sortedRows(): any[] {
    return [...(this.rows || [])].sort(
      (a, b) => new Date(a?.date ?? 0).getTime() - new Date(b?.date ?? 0).getTime(),
    );
  }

  getTime(date: any): number {
    return date ? new Date(date).getTime() : 0;
  }

  getTitle(row: any): string {
    return this.titlesById[row?.achievementId] || '';
  }

  async loadTitles() {
    const ids = Array.from(
      new Set(
        (this.rows || [])
          .map((r: any) => r?.achievementId)
          .filter((id: any) => id !== null && id !== undefined && this.titlesById[id] === undefined),
      ),
    );
    if (ids.length === 0) return;
    try {
      const result = await this.dataService.getAllOfTypeAsync(
        'achievements',
        {
          query: { id: { $in: ids } },
          select: '_id id title',
        },
      );
      for (const achievement of result || []) {
        this.titlesById[achievement.id] = achievement.title;
      }
    } catch {
      // Leave titles unresolved; the id link is still shown.
    }
  }

  async searchAchievements(event: any) {
    const query = event.query ?? '';
    try {
      const numericId = query && !isNaN(Number(query)) ? Number(query) : null;
      const searchQuery = query
        ? { $or: [{ title: { $regex: query, $options: 'i' } }, ...(numericId !== null ? [{ id: numericId }] : [])] }
        : {};
      const result = await this.dataService.getAllOfTypeAsync(
        'achievements',
        {
          query: searchQuery,
          select: '_id id title',
          sort: { title: 1 },
        }
      );
      this.searchResults = result || [];
    } catch {
      this.searchResults = [];
    }
  }

  selectAchievement(event: any) {
    this.newAchievement = event?.value ?? event;
  }

  addRow() {
    this.errorMessage = null;

    if (!this.newDate) {
      this.errorMessage = 'Select a date/time.';
      return;
    }
    if (this.newAchievement?.id === null || this.newAchievement?.id === undefined) {
      this.errorMessage = 'Select an achievement.';
      return;
    }
    if ((this.rows || []).some((r: any) => this.getTime(r.date) === this.getTime(this.newDate))) {
      this.errorMessage = 'That date/time is already assigned an achievement.';
      return;
    }

    this.titlesById[this.newAchievement.id] = this.newAchievement.title;
    const updated = [
      ...(this.rows || []),
      { date: this.newDate, achievementId: this.newAchievement.id },
    ];
    this.onChange.emit(updated);
    this.newDate = null;
    this.newAchievement = null;
    this.searchResults = [];
  }

  removeRow(date: any) {
    const target = this.getTime(date);
    const updated = (this.rows || []).filter((r: any) => this.getTime(r.date) !== target);
    this.onChange.emit(updated);
  }
}
